import {
	Breakpoints,
	Container,
	ContainerPadding,
	CTA,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { ChevronRight, Service } from '@volkswagen-onehub/icons-core';
import { Dealers } from '@esportal/portal-service';
import { AccordionStyled } from './accordion-styled';
import { ServiceType } from '../detail-layer/services-layer-types';

interface ServicesDealerProps {
	dealers: Dealers;
	isOpen: boolean;
	onToggle: () => void;
	isPreviousOpen: boolean;
	openServicesLayer: (type: ServiceType) => void;
}

const useIsMax959 = (maxWidth = 959): boolean => {
	const [isMax959, setIsMax959] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMax959(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);

	return isMax959;
};

export const ServicesDealer = ({
	dealers,
	isOpen,
	onToggle,
	isPreviousOpen,
	openServicesLayer,
}: ServicesDealerProps): React.ReactElement => {
	const isMax959 = useIsMax959();

	const servicesList = [
		dealers.services.recogidaEntrega && {
			key: ServiceType.RecogidaEntrega,
			title: 'Recogida y entrega a domicilio',
			description: 'Recogemos tu coche para llevarlo al taller y lo devolvemos en el punto que nos indiques.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		dealers.services.cocheSustitucion && {
			key: ServiceType.CocheSustitucion,
			title: 'Coche de sustitución',
			description: 'Pide un coche de sustitución para seguir con tu día a día mientras el tuyo está en el taller.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		dealers.services.expressService && {
			key: ServiceType.ExpressService,
			title: 'Express Service',
			description:
				'Sabemos que tu tiempo es oro. Por eso, con este servicio dejamos tu Volkswagen listo en 60 minutos.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		dealers.services.autoservicio && {
			key: ServiceType.Autoservicio,
			title: 'Autoservicio',
			description: 'Tú decides cuándo dejar o recoger tu Volkswagen del taller con las taquillas 24h.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		dealers.services.ITVporTi && {
			key: ServiceType.ITVporTi,
			title: 'Pasamos la ITV por ti',
			description: 'Si no tienes tiempo de pasar la ITV, nos encargamos nosotros de llevar tu coche por solo 10€.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		{
			key: ServiceType.PreItv,
			title: 'Revisión pre-ITV',
			description: 'Revisamos tu coche antes de la ITV para que la pases con total tranquilidad.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
		{
			key: ServiceType.CheckeoCortesia,
			title: 'VW Check',
			description: 'Cada vez que pases por taller, le haremos una revisión general a tu coche sin coste.',
			icon: <ChevronRight variant="small" aria-hidden />,
		},
	].filter(Boolean) as {
		key: ServiceType;
		title: string;
		description: string;
		icon: React.ReactElement;
	}[];

	return (
		<AccordionStyled
			icon={<Service variant="default" />}
			title={`Servicios ofrecidos por ${dealers.commercialName}`}
			subtitle="Descubre qué servicios ofrece tu taller para cuidar de tu coche y que sigas en movimiento."
			isOpen={isOpen}
			onToggle={onToggle}
			isPreviousOpen={isPreviousOpen}
		>
			<Container
				wrap="always"
				padding={{
					left: ContainerPadding.grid001,
					right: ContainerPadding.grid001,
				}}
			>
				<Layout
					appearance={{
						[Breakpoints.default]: [
							{ name: '.', columns: 0 },
							{ name: 'a', columns: 24 },
							{ name: '.', columns: 0 },
						],
					}}
				>
					<Text
						appearance={isMax959 ? TokenTextAppearance.copy200 : TokenTextAppearance.bigcopy100}
						textAlign={TextAlignment.left}
					>
						Estos son los servicios disponibles en tu taller oficial Volkswagen.
					</Text>
				</Layout>
			</Container>

			<Layout
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 24 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b560]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 24 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b960]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 12 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 12 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b1280]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 12 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 12 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b1600]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 8 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 8 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'c', columns: 8 },
						{ name: '.', columns: 0 },
					],
				}}
			>
				{servicesList.map((service) => (
					<Container
						key={service.key}
						wrap="always"
						padding={{
							top: ContainerPadding.dynamic0150,
							left: ContainerPadding.grid001,
							right: ContainerPadding.grid001,
						}}
					>
						<Text appearance={TokenTextAppearance.headline200} textAlign={TextAlignment.left} bold>
							{service.title}
						</Text>

						<Container padding={{ top: ContainerPadding.static150 }}>
							<Text appearance={TokenTextAppearance.label200} textAlign={TextAlignment.left}>
								{service.description}
							</Text>
						</Container>

						<Container
							padding={{
								top: isMax959 ? ContainerPadding.dynamic0050 : ContainerPadding.dynamic0100,
								bottom: ContainerPadding.static100,
							}}
						>
							<CTA
								tag="button"
								emphasis="secondary"
								size="small"
								onClick={() => openServicesLayer(service.key)}
								aria-label={`Saber más sobre ${service.title}`}
								icon={service.icon}
								iconPosition="end"
							>
								Saber más
							</CTA>
						</Container>
					</Container>
				))}
			</Layout>
		</AccordionStyled>
	);
};
